import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/utils2";
import { useAutoTranslate, TranslationBadge } from "@/components/AutoTranslate";

const STATUS = ["Nouveau", "Vu", "En cours", "Resolu"];
const STATUS_COLOR = { Nouveau: "#3254C6", Vu: "#F59E0B", "En cours": "#C05A46", Resolu: "#10B981" };

export default function Tickets() {
  const { t, lang } = useApp();
  const { isRole } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);

  const load = useCallback(() =>
    api.get("/tickets").then((r) => setTickets(r.data)).catch(() => toast.error(t("load_failed"))), [t]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await api.put(`/tickets/${id}`, { status });
    toast.success("OK"); load();
  };

  const canManage = isRole("president", "gardien");

  return (
    <div className="space-y-4" data-testid="tickets-page">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate("/app")} className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center" data-testid="tickets-back">
          <Icons.ArrowLeft className="h-5 w-5 rtl:rotate-180" />
        </button>
        <h1 className="text-2xl font-bold flex-1">{t("tickets")}</h1>
        <button onClick={() => navigate("/action")} className="h-10 px-4 rounded-full bg-terracotta text-white font-semibold text-sm flex items-center gap-1" data-testid="new-ticket-btn">
          <Icons.Plus className="h-4 w-4" /> {t("new_ticket")}
        </button>
      </div>

      {tickets.length === 0 && (
        <div className="py-16 text-center">
          <Icons.PartyPopper className="h-12 w-12 mx-auto text-success mb-3" />
          <p className="text-muted-foreground">{t("no_tickets")} 🎉</p>
        </div>
      )}

      <div className="space-y-3">
        {tickets.map((tk) => (
          <TicketCard key={tk.id} tk={tk} t={t} lang={lang} canManage={canManage} onStatus={setStatus} />
        ))}
      </div>
    </div>
  );
}

function TicketCard({ tk, t, lang, canManage, onStatus }) {
  const tr = useAutoTranslate({ description: tk.description }, tk.lang);
  return (
    <div className="rounded-2xl bg-card border border-border p-4" data-testid={`ticket-${tk.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-bold">{t("tcat_" + tk.category)}</p>
          <p className="text-sm text-muted-foreground">{tr.values.description}</p>
          <TranslationBadge tr={tr} />
          {tk.location && <p className="text-xs text-muted-foreground mt-1"><Icons.MapPin className="h-3 w-3 inline" /> {tk.location}</p>}
        </div>
        <span className="text-xs font-bold text-white px-2.5 py-1 rounded-full whitespace-nowrap" style={{ backgroundColor: STATUS_COLOR[tk.status] }}>
          {t("st_" + tk.status)}
        </span>
      </div>
      <p className="text-xs text-muted-foreground mt-2">{tk.author} · {fmtDate(tk.created_at, lang)}{tk.assigned_to && ` · ${t("assigned_to")}: ${tk.assigned_to}`}</p>
      {canManage && (
        <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar">
          {STATUS.map((s) => (
            <button key={s} onClick={() => onStatus(tk.id, s)} data-testid={`ticket-status-${tk.id}-${s}`}
              className={`h-8 px-3 rounded-full text-xs font-semibold whitespace-nowrap ${tk.status === s ? "bg-brand text-white" : "bg-secondary text-secondary-foreground"}`}>
              {t("st_" + s)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
